import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED, type RealtimeManager } from '@/shared/realtime';

import { goalKeys, type DragGoal, type MapGraph, type MapViewer } from './api';
import { usePresenceStore } from './model/presence';

/** Realtime event names of the goals module (backend `GoalsEvents`, `MapPresence`). */
export const GOALS_EVENTS = {
  mapChanged: 'goals.map-changed',
  mapDeleted: 'goals.map-deleted',
  mapsChanged: 'goals.maps-changed',
  commentCreated: 'goals.comment-created',
  commentUpdated: 'goals.comment-updated',
  commentDeleted: 'goals.comment-deleted',
  goalsRefreshed: 'goals.goals-refreshed',
  presenceChanged: 'goals.presence-changed',
  goalsDragging: 'goals.goals-dragging',
} as const;

export interface PresencePayload {
  mapId: string;
  viewers: MapViewer[];
}

export interface DraggingPayload {
  mapId: string;
  userId: string;
  clientId: string;
  goals: DragGoal[];
  done: boolean;
}

export interface MapChangedPayload {
  mapId: string;
  revision: number;
  actorId: string | null;
  reason: string;
  goalIds: string[];
}

export interface MapListPayload {
  mapId: string;
  actorId: string | null;
  reason: string;
}

export interface CommentPayload {
  mapId: string;
  goalId: string;
  commentId: string;
  authorId: string;
}

/** Maps deleted while this tab was open (the canvas says so instead of "not found"). */
const deletedMaps = new Set<string>();
export const isMapDeleted = (id: string) => deletedMaps.has(id);

/** Which cached queries an event makes stale (pure, unit tested). */
export function staleQueriesFor(
  type: string,
  payload: Partial<MapChangedPayload & CommentPayload> | null,
  cachedRevision?: number,
): (readonly unknown[])[] {
  const keys: (readonly unknown[])[] = [];
  const mapId = payload?.mapId;
  switch (type) {
    case GOALS_EVENTS.mapChanged:
      if (mapId) {
        // An event the cache already reflects (our own change, answered with its revision) needs no refetch.
        if (cachedRevision === undefined || (payload.revision ?? 0) > cachedRevision)
          keys.push(goalKeys.map(mapId));
        keys.push(goalKeys.mapActivity(mapId));
      }
      for (const goalId of payload?.goalIds ?? [])
        keys.push(goalKeys.goal(goalId), goalKeys.activity(goalId), goalKeys.tasks(goalId));
      keys.push(goalKeys.lists(), goalKeys.counters, ['goals', 'person']);
      break;
    case GOALS_EVENTS.goalsRefreshed:
      // Not covered by the map's revision (a deadline passed, a linked task changed): always refetch.
      if (mapId && payload.reason !== 'tasks') keys.push(goalKeys.map(mapId));
      for (const goalId of payload?.goalIds ?? []) keys.push(goalKeys.goal(goalId), goalKeys.tasks(goalId));
      if (payload?.reason !== 'tasks') keys.push(goalKeys.counters, ['goals', 'person']);
      break;
    case GOALS_EVENTS.mapDeleted:
    case GOALS_EVENTS.mapsChanged:
      keys.push(goalKeys.lists(), goalKeys.counters, ['goals', 'person']);
      break;
    case GOALS_EVENTS.commentCreated:
    case GOALS_EVENTS.commentUpdated:
    case GOALS_EVENTS.commentDeleted:
      if (payload?.goalId) keys.push(goalKeys.comments(payload.goalId), goalKeys.goal(payload.goalId));
      break;
    default:
      break;
  }
  return keys;
}

/**
 * Keeps the goals caches live: `goals.*` events (the topic `map:<id>` of an open canvas or card, and the user's own
 * list events) invalidate what they affect; a revoked topic drops the map the user may no longer see; a reconnect
 * refetches everything.
 */
export function registerGoalsRealtime(realtime: RealtimeManager, queryClient: QueryClient): () => void {
  const offEvents = realtime.on<Record<string, unknown> | null>('goals.*', (payload, envelope) => {
    const presence = usePresenceStore.getState();
    if (envelope.type === GOALS_EVENTS.presenceChanged) {
      const data = payload as PresencePayload | null;
      if (data?.mapId) presence.setViewers(data.mapId, data.viewers);
      return;
    }
    if (envelope.type === GOALS_EVENTS.goalsDragging) {
      const data = payload as DraggingPayload | null;
      if (data?.mapId) presence.applyDrag(data.mapId, data.userId, data.clientId, data.goals, data.done);
      return;
    }
    const data = payload as Partial<MapChangedPayload & CommentPayload> | null;
    // A saved move ends the previews of that person's drag — once the map refetched at its revision is shown.
    if (envelope.type === GOALS_EVENTS.mapChanged && data?.mapId && data.actorId) {
      presence.settleDrags(data.mapId, data.actorId, data.revision ?? 0);
    }
    if (envelope.type === GOALS_EVENTS.mapDeleted && data?.mapId) {
      deletedMaps.add(data.mapId);
      void queryClient.resetQueries({ queryKey: goalKeys.map(data.mapId) });
    }
    const cached = data?.mapId
      ? queryClient.getQueryData<MapGraph>(goalKeys.map(data.mapId))?.revision
      : undefined;
    for (const queryKey of staleQueriesFor(envelope.type, data, cached)) {
      void queryClient.invalidateQueries({ queryKey });
    }
  });
  const offRevoked = realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) => {
    const topic = payload?.topic;
    if (!topic?.startsWith('map:')) return;
    const mapId = topic.slice('map:'.length);
    void queryClient.resetQueries({ queryKey: goalKeys.map(mapId) });
    void queryClient.resetQueries({ queryKey: goalKeys.goals() });
    void queryClient.invalidateQueries({ queryKey: goalKeys.lists() });
  });
  const offReconnect = realtime.on(REALTIME_RECONNECTED, () => {
    void queryClient.invalidateQueries({ queryKey: goalKeys.all });
  });
  return () => {
    offEvents();
    offRevoked();
    offReconnect();
  };
}
